// src/components/programs/ProgramsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme, Button } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import useGetPrograms from "../../hooks/programs/useGetPrograms";
import useDeleteProgram from "../../hooks/programs/useDeleteProgram";
import useDeleteAllPrograms from "../../hooks/programs/useDeleteAllPrograms";
import useRestoreProgram from "../../hooks/programs/useRestoreProgram";
import useRestoreAllPrograms from "../../hooks/programs/useRestoreAllPrograms";
import useForceDeleteProgram from "../../hooks/programs/useForceDeleteProgram";
import useForceDeleteAllPrograms from "../../hooks/programs/useForceDeleteAllPrograms";
import useConfirmProgram from "../../hooks/programs/useConfirmProgram";
import useCloseProgram from "../../hooks/programs/useCloseProgram";
import useExportProgramToPdf from "../../hooks/programs/useExportProgramToPdf";
import useConvertProgramCurrency from "../../hooks/programs/useConvertProgramCurrency";
import PrintProgram from "./ShowProgram/PrintProgram";
import ReactDOMServer from "react-dom/server";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ProgramsTableRow from "./Table/ProgramsTableRow";
import ProgramsTableColumns from "./Table/ProgramsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";
import useGetCurrencies from "../../hooks/currencies/useGetCurrencies";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

const ProgramsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("programs_list"),
            create: has("programs_create"),
            edit: has("programs_edit"),
            delete: has("programs_delete"),
            forceDelete: has("programs_force_delete"),
            archiveList: has("programs_archive_list"),
            restore: has("programs_restore"),
            restoreAll: has("programs_restore-all-table"),
            deleteAll: has("programs_delete-all-table"),
            massForceDeleteAll: has("programs_mass_force_delete-all-table"),
            view: has("programs_view"),
            confirm: has("programs_confirm"),
            close: has("programs_close"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/programs/archive");

    const [selectedCurrency, setSelectedCurrency] = React.useState("");
    const [bulkConverting, setBulkConverting] = React.useState(false);

    // CRUD Hooks
    const { deleteProgramMutate, isDelete } = useDeleteProgram();
    const { deleteAllProgramsMutate, isDeleteAll } = useDeleteAllPrograms();
    const { restoreProgramMutate, isRestore } = useRestoreProgram();
    const { restoreAllProgramsMutate, isRestoreAll } = useRestoreAllPrograms();
    const { forceDeleteProgramMutate, isForceDelete } = useForceDeleteProgram();
    const { forceDeleteAllProgramsMutate, isForceDeleteAll } = useForceDeleteAllPrograms();
    const { confirmProgramMutate, isConfirm } = useConfirmProgram();
    const { closeProgramMutate, isClose } = useCloseProgram();
    const { exportPdfMutate, isExporting } = useExportProgramToPdf();
    const { convertCurrencyMutate } = useConvertProgramCurrency();
    const { currencies, isLoading: isLoadingCurrencies } = useGetCurrencies(1, 1000); // Get all currencies

    // Export function
    const handleExportProgram = (programId) => {
        // Find the program from items
        const program = items.find(p => p.id === programId);
        if (!program) return;

        if (!program.confirmed) {
            toast.error(isArabic ? "لا يمكن تصدير البرنامج إلا بعد التأكيد" : "Program must be confirmed to export PDF");
            return;
        }

        // Generate HTML content
        const htmlContent = ReactDOMServer.renderToString(
            <PrintProgram program={program} lang={lng} />
        );

        // Export data
        const exportData = {
            html_content: htmlContent,
            filename: `${program.title?.[lng] || 'program'}.pdf`,
            // Add default values for other fields if needed
            customer_name: program.customer_title || '',
            customer_phone: '',
            customer_email: '',
            customer_nationality: '',
            customer_country: '',
            include_terms: true,
            include_policies: true,
        };

        exportPdfMutate({ id: programId, data: exportData });
    };

    // Bulk convert currency
    const handleBulkConvertCurrency = () => {
        if (!selectedIds.length || !selectedCurrency) {
            toast.error(isArabic ? "الرجاء تحديد البرامج والعملة" : "Please select programs and currency");
            return;
        }

        setBulkConverting(true);
        
        // Convert each selected program
        selectedIds.forEach(id => {
            convertCurrencyMutate({ id, currencyId: selectedCurrency });
        });

        // Reset after a short delay
        setTimeout(() => {
            setBulkConverting(false);
            setSelectedIds([]);
            setSelectedCurrency("");
        }, 1000);
    };

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleMenuConfirm,
        handleMenuCloseAction,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
        handleResetSort,
    } = useTableActions({
        basePath: "/programs",
        deleteSingle: deleteProgramMutate,
        deleteAll: deleteAllProgramsMutate,
        restoreSingle: restoreProgramMutate,
        restoreAll: restoreAllProgramsMutate,
        forceDeleteSingle: forceDeleteProgramMutate,
        forceDeleteAll: forceDeleteAllProgramsMutate,
        confirmSingle: confirmProgramMutate,
        closeSingle: closeProgramMutate,
        exportSingle: handleExportProgram,
        isArchive,
        // programs table should default to sorting by updated_at instead of id
        defaultSortBy: "updated_at",
    });

    const filters = useMemo(
        () => ({
            search: search || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, sortBy, sortDir, isArchive]
    );

    const { isLoading, error, programs, meta } = useGetPrograms(page, perPage, filters);

    const items = useMemo(() => programs?.items || [], [programs]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete && !perms.view;

    const renderRow = (params) => (
        <ProgramsTableRow
            key={params.item.id}
            program={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "البرامج السياحية" : "Programs";
    const subtitle = isArchive
        ? isArabic
            ? "عرض البرامج المحذوفة (الأرشيف)"
            : "Archived programs (soft deleted)"
        : isArabic
            ? "إدارة البرامج السياحية المتاحة في النظام"
            : "Manage available programs in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة برنامج"
            : "Create new program";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllProgramsMutate({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/programs/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                    spacing={2}
                    alignItems="center"
                >
                    {/* Currency Convert Section */}
                    {selectedIds.length > 0 && (
                        <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>
                                    {isArabic ? "تحويل العملة" : "Convert Currency"}
                                </InputLabel>
                                <Select
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                    disabled={isLoadingCurrencies || bulkConverting}
                                    label={isArabic ? "تحويل العملة" : "Convert Currency"}
                                >
                                    {currencies?.items?.map((currency) => (
                                        <MenuItem key={currency.id} value={currency.id}>
                                            {currency.symbol} {currency.name[lng] || currency.name.en}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleBulkConvertCurrency}
                                disabled={!selectedCurrency || bulkConverting}
                                color="info"
                            >
                                {bulkConverting 
                                    ? (isArabic ? "جاري التحويل..." : "Converting...") 
                                    : (isArabic ? "تحويل" : "Convert")
                                }
                            </Button>
                        </>
                    )}

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleResetSort}
                        color="inherit"
                        title={isArabic ? "إعادة تعيين الترتيب إلى الافتراضي (تاريخ التحديث)" : "Reset Sorting to Default (Updated At)"}
                        startIcon={<RestartAltIcon />}
                    >
                        {isArabic ? "إعادة تعيين الترتيب" : "Reset Sort"}
                    </Button>

                    <ArchiveButton
                        to="/programs/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/programs"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={ProgramsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                selectedItem={selectedItem}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuView={handleView}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                onMenuConfirm={!isArchive && !selectedItem?.confirmed ? handleMenuConfirm : undefined}
                onMenuCloseAction={!isArchive && selectedItem?.confirmed && !selectedItem?.closed ? handleMenuCloseAction : undefined}
                menuDisableEdit={!isArchive && (!perms.edit || selectedItem?.closed)}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                menuDisableConfirm={!isArchive && !perms.confirm}
                menuDisableClose={!isArchive && (!perms.close || selectedItem?.closed)}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete || isConfirm || isClose || isExporting}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد برامج في الأرشيف" : "لا توجد برامج"}
                emptyLabelEn={isArchive ? "No archived programs" : "No programs"}
                loadErrorAr="حدث خطأ أثناء تحميل البرامج"
                loadErrorEn="Failed to load programs"
            />
        </>
    );
};

export default ProgramsTable;
